/**
 * pendingInvite: a small localStorage-backed handoff for completing an invite
 * join after authentication (#171 Phase 2).
 *
 * When a logged-out recipient clicks "Sign up" on the invite banner, the
 * ecosystem slug is stashed here. Signup requires email verification, so the
 * actual join can only happen once they authenticate — potentially in a new
 * tab or later — which is why this uses localStorage (durable) rather than
 * sessionStorage. The post-auth EcosystemChooser reads it, joins that
 * ecosystem, and clears it.
 *
 * A short TTL guards against a stale entry auto-joining an unrelated future
 * signup: if someone clicks Sign up, abandons the flow, and later creates an
 * account for another reason, the expired entry is ignored (and cleared).
 */
const KEY = 'pendingInviteEcosystem';

// How long a pending invite stays actionable after Sign up is clicked. Long
// enough to cover email verification + first login, short enough that an
// abandoned invite doesn't silently join a much-later, unrelated signup.
const TTL_MS = 60 * 60 * 1000; // 1 hour

export const setPendingInvite = (slug) => {
    try {
        window.localStorage.setItem(
            KEY,
            JSON.stringify({ slug, ts: Date.now() })
        );
    } catch {
        /* storage disabled / private mode — non-fatal */
    }
};

export const getPendingInvite = () => {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return null;
        const { slug, ts } = JSON.parse(raw);
        if (!slug || !ts || Date.now() - ts > TTL_MS) {
            window.localStorage.removeItem(KEY);
            return null;
        }
        return slug;
    } catch {
        return null;
    }
};

export const clearPendingInvite = () => {
    try {
        window.localStorage.removeItem(KEY);
    } catch {
        /* non-fatal */
    }
};
